import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { getClinicInfo, updateClinicInfo } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, Save } from 'lucide-react';
import SiteImageField from '../components/SiteImageField';

const FEATURE_ICON_OPTIONS = [
  'shield',
  'heart',
  'clock',
  'calendar',
  'sparkles',
  'star',
  'thumbs-up',
  'users',
  'award',
  'check-circle',
];

const DEFAULT_FEATURES = [
  { icon: 'shield', title: 'Profesionalismo', desc: 'Personal certificado y capacitado' },
  { icon: 'heart', title: 'Cuidado Integral', desc: 'Atención personalizada para cada paciente' },
  { icon: 'clock', title: 'Puntualidad', desc: 'Respetamos tu tiempo' },
  { icon: 'calendar', title: 'Flexibilidad', desc: 'Horarios que se adaptan a ti' },
];

export default function AdminSitePublic() {
  const { refresh: refreshBranding } = useClinicBranding();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [servicesText, setServicesText] = useState('');
  const [featureRows, setFeatureRows] = useState(DEFAULT_FEATURES);

  useEffect(() => {
    if (!authLoading && user && !isSuperAdmin(user.role)) {
      toast.error('Solo el super administrador puede editar el sitio público.');
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || !isSuperAdmin(user.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await getClinicInfo();
        if (cancelled) return;
        setDraft(d);
        setServicesText(Array.isArray(d.services_bullets) ? d.services_bullets.join('\n') : '');
        if (Array.isArray(d.home_features) && d.home_features.length > 0) {
          setFeatureRows(d.home_features.map((x) => ({ icon: x.icon, title: x.title, desc: x.desc })));
        } else {
          setFeatureRows(DEFAULT_FEATURES);
        }
      } catch {
        toast.error('No se pudo cargar la información de la clínica.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setField = (key, value) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft) return;
    setSaving(true);
    try {
      const services_bullets = servicesText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const payload = {
        ...draft,
        services_bullets,
        home_features: featureRows.map((r) => ({
          icon: r.icon || 'shield',
          title: r.title || '',
          desc: r.desc || '',
        })),
      };
      await updateClinicInfo(payload);
      setDraft(payload);
      await refreshBranding();
      toast.success('Cambios del sitio público guardados.');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading || !draft) {
    return (
      <div className="text-muted-foreground animate-pulse" data-testid="admin-site-loading">
        Cargando editor del sitio…
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-site-public">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Sitio público</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Marca, contacto, textos e imágenes de la página principal. Visible para visitantes al instante.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Marca y contacto</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <SiteImageField
                label="Logotipo"
                url={draft.logo_url}
                onChange={(u) => setField('logo_url', u)}
                inputId="logo_url"
                previewVariant="logo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la empresa / clínica</Label>
              <Input id="name" value={draft.name} onChange={(e) => setField('name', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site_tagline_short">Línea corta (barra superior)</Label>
              <Input
                id="site_tagline_short"
                value={draft.site_tagline_short || ''}
                onChange={(e) => setField('site_tagline_short', e.target.value || null)}
                placeholder="Opcional; si está vacío se usa la dirección resumida"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input id="phone" value={draft.phone} onChange={(e) => setField('phone', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Correo de contacto</Label>
              <Input id="email" type="email" value={draft.email} onChange={(e) => setField('email', e.target.value)} />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input id="address" value={draft.address} onChange={(e) => setField('address', e.target.value)} />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="description">Descripción breve (meta / referencia)</Label>
              <Textarea
                id="description"
                rows={3}
                value={draft.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="footer_blurb">Texto del pie de página (columna izquierda)</Label>
              <Textarea
                id="footer_blurb"
                rows={3}
                value={draft.footer_blurb || ''}
                onChange={(e) => setField('footer_blurb', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="copyright_name">Nombre en copyright</Label>
              <Input
                id="copyright_name"
                value={draft.copyright_name || ''}
                onChange={(e) => setField('copyright_name', e.target.value || null)}
                placeholder="Por defecto: nombre de la empresa"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hero (inicio)</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="hero_badge_text">Texto del distintivo (badge)</Label>
              <Input
                id="hero_badge_text"
                value={draft.hero_badge_text || ''}
                onChange={(e) => setField('hero_badge_text', e.target.value || null)}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hero_title_prefix">Título — parte antes del acento</Label>
                <Input
                  id="hero_title_prefix"
                  value={draft.hero_title_prefix || ''}
                  onChange={(e) => setField('hero_title_prefix', e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hero_title_highlight">Título — palabras en color primario</Label>
                <Input
                  id="hero_title_highlight"
                  value={draft.hero_title_highlight || ''}
                  onChange={(e) => setField('hero_title_highlight', e.target.value || null)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_subtitle">Subtítulo</Label>
              <Textarea
                id="hero_subtitle"
                rows={3}
                value={draft.hero_subtitle || ''}
                onChange={(e) => setField('hero_subtitle', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <SiteImageField
                label="Imagen principal (hero)"
                url={draft.hero_image_url}
                onChange={(u) => setField('hero_image_url', u)}
                inputId="hero_image_url"
                previewVariant="hero"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tarjetas de valores (4 columnas)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {featureRows.map((row, i) => (
              <div key={i} className="grid gap-3 p-4 border border-border rounded-xl sm:grid-cols-12 items-end">
                <div className="sm:col-span-3 space-y-2">
                  <Label>Icono (Lucide)</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={row.icon}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, icon: v } : r)));
                    }}
                  >
                    {FEATURE_ICON_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-4 space-y-2">
                  <Label>Título</Label>
                  <Input
                    value={row.title}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, title: v } : r)));
                    }}
                  />
                </div>
                <div className="sm:col-span-4 space-y-2">
                  <Label>Descripción</Label>
                  <Input
                    value={row.desc}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFeatureRows((rows) => rows.map((r, j) => (j === i ? { ...r, desc: v } : r)));
                    }}
                  />
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => setFeatureRows((rows) => rows.filter((_, j) => j !== i))}
                    disabled={featureRows.length <= 1}
                    aria-label="Eliminar fila"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => setFeatureRows((rows) => [...rows, { icon: 'shield', title: '', desc: '' }])}
            >
              <Plus className="w-4 h-4" />
              Añadir tarjeta
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Servicios (lista con viñetas)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="services_section_title">Título de sección</Label>
                <Input
                  id="services_section_title"
                  value={draft.services_section_title || ''}
                  onChange={(e) => setField('services_section_title', e.target.value || null)}
                />
              </div>
              <div className="sm:col-span-2">
                <SiteImageField
                  label="Imagen lateral (sección servicios)"
                  url={draft.clinic_section_image_url}
                  onChange={(u) => setField('clinic_section_image_url', u)}
                  inputId="clinic_section_image_url"
                  previewVariant="banner"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="services_section_intro">Párrafo introductorio</Label>
              <Textarea
                id="services_section_intro"
                rows={3}
                value={draft.services_section_intro || ''}
                onChange={(e) => setField('services_section_intro', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="services_bullets">Ítems (uno por línea)</Label>
              <Textarea id="services_bullets" rows={8} value={servicesText} onChange={(e) => setServicesText(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Secciones inferiores</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="featured_products_title">Título — productos destacados</Label>
              <Input
                id="featured_products_title"
                value={draft.featured_products_title || ''}
                onChange={(e) => setField('featured_products_title', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reviews_section_title">Título — reseñas</Label>
              <Input
                id="reviews_section_title"
                value={draft.reviews_section_title || ''}
                onChange={(e) => setField('reviews_section_title', e.target.value || null)}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="featured_products_intro">Intro — productos destacados</Label>
              <Textarea
                id="featured_products_intro"
                rows={2}
                value={draft.featured_products_intro || ''}
                onChange={(e) => setField('featured_products_intro', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_title">CTA — título</Label>
              <Input id="cta_title" value={draft.cta_title || ''} onChange={(e) => setField('cta_title', e.target.value || null)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta_subtitle">CTA — subtítulo</Label>
              <Input
                id="cta_subtitle"
                value={draft.cta_subtitle || ''}
                onChange={(e) => setField('cta_subtitle', e.target.value || null)}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="contact_section_title">Título bloque contacto (abajo)</Label>
              <Input
                id="contact_section_title"
                value={draft.contact_section_title || ''}
                onChange={(e) => setField('contact_section_title', e.target.value || null)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Horarios (texto libre)</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-1">
            <div className="space-y-2">
              <Label htmlFor="footer_hours_weekdays">Semana</Label>
              <Input
                id="footer_hours_weekdays"
                value={draft.footer_hours_weekdays || ''}
                onChange={(e) => setField('footer_hours_weekdays', e.target.value || null)}
                placeholder="Lunes a Viernes: 9:00 AM - 5:00 PM"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="footer_hours_lunch">Comida</Label>
              <Input
                id="footer_hours_lunch"
                value={draft.footer_hours_lunch || ''}
                onChange={(e) => setField('footer_hours_lunch', e.target.value || null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="footer_hours_weekend">Fin de semana</Label>
              <Input
                id="footer_hours_weekend"
                value={draft.footer_hours_weekend || ''}
                onChange={(e) => setField('footer_hours_weekend', e.target.value || null)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="gap-2 rounded-full">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}
